import React, { useState } from "react";
import { UploadSimple, MagicWand, CircleNotch } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import {
  analyzeContract,
  uploadHuurcontract,
  ExtractionContext,
  PreviewDocument,
} from "./contractExtractionApi";
import { ExtractedContractDto } from "./types";

interface ContractDocumentAnalyzerProps {
  ctx: ExtractionContext;
  session: any;
  /** Called with the prefill payload once Ella has read the contract. */
  onExtracted: (data: ExtractedContractDto) => void;
  /** Called right after upload with the stored document (for the PDF preview). */
  onDocumentReady?: (doc: PreviewDocument) => void;
  /** Disable the zone (e.g. no unit selected yet) with an explanatory hint. */
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Drag & drop / upload zone for a Huurcontract. On drop it uploads the document
 * (so it is stored + labelled "Huurcontract"), then asks Ella to read it out
 * and hands the extracted data back to the parent for prefilling the form.
 */
const ContractDocumentAnalyzer: React.FC<ContractDocumentAnalyzerProps> = ({
  ctx,
  session,
  onExtracted,
  onDocumentReady,
  disabled = false,
  disabledReason,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusText, setStatusText] = useState("");

  const handleFile = async (file: File) => {
    if (!file || disabled || isProcessing) return;

    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Bestand is te groot. Maximum toegestane grootte is 20 MB.");
      return;
    }

    setIsProcessing(true);
    try {
      setStatusText("Huurcontract uploaden…");
      const uploaded = await uploadHuurcontract(file, ctx, session);
      // Show the PDF in the preview pane straight away (before analysis finishes).
      onDocumentReady?.({
        sharePointFileId: uploaded.sharePointFileId,
        sharePointFileUrl: uploaded.sharePointFileUrl,
        name: uploaded.name,
      });

      setStatusText("Ella leest het huurcontract uit…");
      const data = await analyzeContract(uploaded.fileLogId, ctx, session);

      onExtracted(data);
      toast.success("Ella heeft het huurcontract uitgelezen.");
    } catch (error: any) {
      console.error("Error analyzing rental contract:", error);
      toast.error(error?.message || "Het huurcontract kon niet worden uitgelezen.");
    } finally {
      setIsProcessing(false);
      setStatusText("");
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || isProcessing) return;
    const files = event.dataTransfer.files;
    if (files.length) handleFile(files[0]);
  };

  return (
    <div className="mb-4">
      <div
        className={`relative rounded-lg border-2 border-dashed p-5 text-center transition-colors ${
          disabled
            ? "border-gray-200 bg-gray-50"
            : "border-blue-300 bg-blue-50 hover:border-blue-400"
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="application/pdf,image/*"
          disabled={disabled || isProcessing}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
            e.target.value = "";
          }}
          className="hidden"
          id="huurcontract-analyzer-input"
        />

        {isProcessing ? (
          <div className="flex flex-col items-center justify-center py-2 text-blue-700">
            <CircleNotch size={32} className="mb-2 animate-spin" />
            <p className="text-sm font-medium">{statusText}</p>
          </div>
        ) : (
          <label
            htmlFor="huurcontract-analyzer-input"
            className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
          >
            <div className="mb-2 flex items-center justify-center gap-2 text-blue-700">
              <MagicWand size={22} weight="fill" />
              <UploadSimple size={22} />
            </div>
            <p className="text-sm font-medium text-gray-800">
              Sleep het huurcontract hierheen of klik om te uploaden
            </p>
            <p className="mt-1 text-xs text-gray-600">
              Ella leest automatisch de prijs, datums, regime en contacten uit (PDF, max. 20 MB)
            </p>
            {disabled && disabledReason && (
              <p className="mt-2 text-xs font-medium text-amber-700">{disabledReason}</p>
            )}
          </label>
        )}
      </div>
    </div>
  );
};

export default ContractDocumentAnalyzer;
